import type { SliceConfig } from './types';
import type { Maybe } from '@/src/types';

import { defineStore } from 'pinia';
import { reactive } from 'vue';
import z from 'zod';

import { clampValue } from '@/src/utils';
import {
  deleteSecondKey,
  doubleKeySchema,
  getDoubleKeyRecord,
  patchDoubleKeyRecord,
} from '@/src/utils/doubleKeyRecord';

import { SliceConfigSchema } from './types';

export const defaultSliceConfig = (): SliceConfig => ({
  slice: 0,
  min: 0,
  max: 1,
  axisDirection: 'Inferior',
});

export const SerializedViewSliceStoreSchema = doubleKeySchema(SliceConfigSchema);
export type SerializedViewSliceStore = z.infer<typeof SerializedViewSliceStoreSchema>;

export const useViewSliceStore = defineStore('viewSlice', () => {
  const configs = reactive<SerializedViewSliceStore>({});

  const getConfig = (viewID: Maybe<string>, dataID: Maybe<string>) =>
    getDoubleKeyRecord(configs, viewID, dataID);

  const updateConfig = (
    viewID: string,
    dataID: string,
    patch: Partial<SliceConfig>,
  ) => {
    const config = {
      ...defaultSliceConfig(),
      ...getConfig(viewID, dataID),
      ...patch,
    };

    config.slice = clampValue(config.slice, config.min, config.max);
    patchDoubleKeyRecord(configs, viewID, dataID, config);
  };

  const resetSlice = (viewID: string, dataID: string) => {
    const config = getConfig(viewID, dataID);
    if (!config) return;

    // Setting this to floor() will affect images where the
    // middle slice is fractional.
    // This is consistent with vtkImageMapper and SliceRepresentationProxy.
    updateConfig(viewID, dataID, {
      slice: Math.ceil((config.min + config.max) / 2),
    });
  };

  const removeView = (viewID: string) => {
    delete configs[viewID];
  };

  const removeData = (dataID: string, viewID?: string) => {
    if (viewID) {
      delete configs[viewID]?.[dataID];
    }
    else {
      deleteSecondKey(configs, dataID);
    }
  };

  const serialize = (): SerializedViewSliceStore => JSON.parse(JSON.stringify(configs)) as SerializedViewSliceStore;

  const deserialize = (serialized?: SerializedViewSliceStore) => {
    if (!serialized) return;
    Object.entries(serialized).forEach(([viewID, byData]) => {
      Object.entries(byData).forEach(([dataID, config]) => {
        updateConfig(viewID, dataID, config);
      });
    });
  };

  return {
    configs,
    getConfig,
    updateConfig,
    resetSlice,
    removeView,
    removeData,
    serialize,
    deserialize,
  };
});

export default useViewSliceStore;
